import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Users, Globe, FileText } from 'lucide-react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-accent-text hover:underline mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Login
          </Link>

          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 text-accent-text mb-4" />
            <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
            <p className="mt-2 text-lg text-muted-foreground">
              How we collect, use, protect, and share your personal data
            </p>
            <p className="text-sm text-muted-foreground mt-2">Last updated: October 2026</p>
            <p className="text-sm text-muted-foreground mt-1">
              Pursuant to the Nigeria Data Protection Act 2023 (NDPA) and Nigeria Data Protection
              Regulation 2019 (NDPR)
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-surface rounded-lg shadow-sm border border-border p-8 prose prose-lg max-w-none space-y-8">

          {/* 1. Identity of Data Controller */}
          <section>
            <div className="flex items-center mb-4">
              <FileText className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">1. Identity of the Data Controller</h2>
            </div>
            <p className="text-muted-foreground">
              <strong>Latlens Technologies</strong> ("we", "us", "our", or "the Company") is the Data
              Controller responsible for your personal data processed through the Enterprise API Gateway
              platform and associated services (collectively, "the Service").
            </p>
            <div className="mt-3 space-y-1 text-muted-foreground">
              <p><strong>Registered Address:</strong> 38b, Ondo Street, New Bodija, Ibadan, Oyo State, Nigeria</p>
              <p><strong>Data Protection Officer:</strong> <a href="mailto:dpo@latlens.com" className="text-accent-text hover:underline">dpo@latlens.com</a></p>
              <p><strong>General Enquiries:</strong> <a href="mailto:privacy@latlens.com" className="text-accent-text hover:underline">privacy@latlens.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@latlens.com" className="text-accent-text hover:underline">support@latlens.com</a></p>
            </div>
          </section>

          {/* 2. Lawful Basis */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">2. Lawful Basis for Processing</h2>
            </div>
            <p className="text-muted-foreground">
              In accordance with Section 25 of the Nigeria Data Protection Act 2023, we process your
              personal data on one or more of the following lawful bases:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li><strong>Contractual necessity:</strong> Processing required to deliver the API Gateway services you have subscribed to.</li>
              <li><strong>Consent:</strong> Where you have given clear, informed, and freely-given consent (e.g., marketing communications).</li>
              <li><strong>Legal obligation:</strong> Where we are required to retain or disclose data to comply with Nigerian law, including the NDPA 2023, FIRS obligations, or valid court orders.</li>
              <li><strong>Legitimate interests:</strong> To detect fraud, ensure network security, and improve service quality, where such interests are not overridden by your fundamental rights.</li>
            </ul>
          </section>

          {/* 3. Data We Collect */}
          <section>
            <div className="flex items-center mb-4">
              <Eye className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">3. Personal Data We Collect</h2>
            </div>

            <h3 className="text-lg font-semibold text-foreground mt-4">3.1 Account Data</h3>
            <p className="text-muted-foreground">
              Full name, email address, date of birth, hashed password, company name, and phone number.
              This data is necessary to create and maintain your account and provide access to the
              Service. We use your date of birth to know whether you need a parent's or guardian's
              permission (see section 11).
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">3.2 API Usage Data</h3>
            <p className="text-muted-foreground">
              Request counts, response times, error rates, API endpoints accessed, IP addresses, and
              approximate geographic location of requests. This data is used to calculate billing,
              enforce rate limits, and provide usage analytics.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">3.3 Billing and Payment Data</h3>
            <p className="text-muted-foreground">
              Billing contact details and transaction references. Payment card data and bank details
              are processed exclusively by our third-party payment processor (Paystack) and are
              never stored on our servers. We receive only a tokenised reference confirming payment.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">3.4 Technical and Log Data</h3>
            <p className="text-muted-foreground">
              Browser type, operating system, referring URLs, and session identifiers collected
              automatically when you access the Service. These are used for security monitoring,
              debugging, and service optimisation.
            </p>

            <h3 className="text-lg font-semibold text-foreground mt-4">3.5 Sensitive Personal Data</h3>
            <p className="text-muted-foreground">
              We do not knowingly collect sensitive personal data (as defined in Section 30 of the
              NDPA 2023) such as health information, biometric data, or religious beliefs. Please do
              not transmit such data through our API endpoints.
            </p>
          </section>

          {/* 4. How We Use Your Data */}
          <section>
            <div className="flex items-center mb-4">
              <Database className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">4. Purposes of Processing</h2>
            </div>
            <p className="text-muted-foreground">We process your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li>Provisioning, maintaining, and improving the API Gateway Service</li>
              <li>Processing payments, issuing invoices, and managing billing cycles</li>
              <li>Providing technical support and responding to your enquiries</li>
              <li>Monitoring API usage to enforce rate limits and prevent abuse</li>
              <li>Sending transactional communications (account confirmations, billing alerts, service notices)</li>
              <li>Detecting, investigating, and preventing fraudulent or unauthorised activity</li>
              <li>Complying with applicable Nigerian law and regulatory obligations</li>
              <li>Sending marketing communications where you have given consent (you may withdraw consent at any time)</li>
            </ul>
          </section>

          {/* 5. Data Security */}
          <section>
            <div className="flex items-center mb-4">
              <Lock className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">5. Data Security Measures</h2>
            </div>
            <p className="text-muted-foreground">
              We implement appropriate technical and organisational security measures in accordance
              with Article 39 of the NDPR 2019 and Section 41 of the NDPA 2023, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li><strong>Encryption in transit:</strong> All data transferred between your browser and our servers is encrypted using TLS 1.3.</li>
              <li><strong>Encryption at rest:</strong> Database records are stored with AES-256 encryption on Cloudflare's global edge network.</li>
              <li><strong>Password security:</strong> Passwords are hashed using PBKDF2-SHA256 with 100,000 iterations and are never stored in plain text.</li>
              <li><strong>Access controls:</strong> Role-based access control limits internal access to personal data on a need-to-know basis. All access is logged.</li>
              <li><strong>DDoS and WAF protection:</strong> Our infrastructure leverages Cloudflare's Web Application Firewall and DDoS mitigation.</li>
              <li><strong>API key security:</strong> API keys are hashed using SHA-256 before storage; only the key prefix is retained for identification.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              In the event of a data breach affecting your rights and freedoms, we will notify the
              Nigeria Data Protection Commission (NDPC) within 72 hours of becoming aware of the
              breach, and will notify affected data subjects without undue delay, in accordance with
              Section 43 of the NDPA 2023.
            </p>
          </section>

          {/* 6. Information Sharing */}
          <section>
            <div className="flex items-center mb-4">
              <Users className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">6. Disclosure and Information Sharing</h2>
            </div>
            <p className="text-muted-foreground">
              We do not sell, trade, or rent your personal data. We may disclose your data only in
              the following limited circumstances:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li><strong>Service providers:</strong> Trusted third-party processors (e.g., Cloudflare for infrastructure, Paystack for payment processing) who act under data processing agreements and are contractually bound to protect your data.</li>
              <li><strong>Legal compliance:</strong> Where required by Nigerian law, a court order, or a request from a competent regulatory authority (including the NDPC, EFCC, or law enforcement).</li>
              <li><strong>Protection of rights:</strong> To protect our rights, property, or safety, or that of our users or the public.</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of company assets, provided the acquiring entity agrees to honour this Privacy Policy.</li>
              <li><strong>With your consent:</strong> For any other purpose with your prior written consent.</li>
            </ul>
          </section>

          {/* 7. International Transfers */}
          <section>
            <div className="flex items-center mb-4">
              <Globe className="h-6 w-6 text-accent-text mr-2" />
              <h2 className="text-2xl font-semibold text-foreground m-0">7. International Data Transfers</h2>
            </div>
            <p className="text-muted-foreground">
              Your data may be processed on Cloudflare's global network, which includes servers
              outside Nigeria. Where personal data is transferred outside Nigeria, we ensure
              appropriate safeguards are in place in accordance with Section 44 of the NDPA 2023,
              including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li>Standard Contractual Clauses (SCCs) approved by the NDPC</li>
              <li>Transfer to countries with an adequate level of data protection as determined by the NDPC</li>
              <li>Binding Corporate Rules where applicable</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              You may request information about the specific safeguards in place for international
              transfers by contacting our Data Protection Officer.
            </p>
          </section>

          {/* 8. Your Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">8. Your Rights as a Data Subject</h2>
            <p className="text-muted-foreground mt-2">
              Under the NDPA 2023 and NDPR 2019, you have the following rights regarding your
              personal data:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li><strong>Right of access (Section 34 NDPA):</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Right to rectification (Section 35 NDPA):</strong> Require us to correct inaccurate or incomplete personal data.</li>
              <li><strong>Right to erasure (Section 36 NDPA):</strong> Request deletion of your personal data where there is no lawful basis for continued processing.</li>
              <li><strong>Right to restriction (Section 37 NDPA):</strong> Request that we restrict the processing of your data in certain circumstances.</li>
              <li><strong>Right to data portability (Section 38 NDPA):</strong> Receive your personal data in a structured, commonly-used, machine-readable format.</li>
              <li><strong>Right to object (Section 39 NDPA):</strong> Object to processing based on legitimate interests or for direct marketing purposes.</li>
              <li><strong>Rights related to automated decision-making (Section 40 NDPA):</strong> Not to be subject to a decision based solely on automated processing that significantly affects you.</li>
              <li><strong>Right to withdraw consent:</strong> Where processing is based on consent, withdraw it at any time without affecting the lawfulness of prior processing.</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:privacy@latlens.com" className="text-accent-text hover:underline">
                privacy@latlens.com
              </a>{' '}
              or write to our Data Protection Officer at the address below. We will respond within
              30 days. If you are dissatisfied with our response, you have the right to lodge a
              complaint with the{' '}
              <strong>Nigeria Data Protection Commission (NDPC)</strong> at{' '}
              <a href="https://ndpb.gov.ng" className="text-accent-text hover:underline" target="_blank" rel="noopener noreferrer">
                ndpb.gov.ng
              </a>.
            </p>
          </section>

          {/* 9. Data Retention */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">9. Data Retention</h2>
            <p className="text-muted-foreground mt-2">
              We retain personal data only as long as necessary for the stated purposes or as required
              by Nigerian law:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li><strong>Account data:</strong> Retained for the duration of your account, plus 7 years thereafter (to meet statutory record-keeping obligations under Nigerian company and tax law).</li>
              <li><strong>API usage logs:</strong> Retained for 13 months for billing reconciliation and fraud detection, then anonymised or deleted.</li>
              <li><strong>Billing records:</strong> Retained for 7 years in compliance with FIRS requirements.</li>
              <li><strong>Security logs:</strong> Retained for 12 months.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              Upon account deletion, your account data will be deleted or anonymised within 30 days,
              except where retention is required by law.
            </p>
          </section>

          {/* 10. Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">10. Cookies and Tracking Technologies</h2>
            <p className="text-muted-foreground mt-2">
              We use the following cookies and similar technologies:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
              <li><strong>Strictly necessary cookies:</strong> Session authentication tokens stored in localStorage, required for the Service to function.</li>
              <li><strong>Security cookies:</strong> Cloudflare security cookies (e.g., <code>__cflb</code>, <code>cf_clearance</code>) to protect against bots and DDoS attacks.</li>
              <li><strong>Turnstile (CAPTCHA):</strong> Cloudflare Turnstile tokens used to verify human users on login and registration forms. No personal data is permanently stored by Turnstile.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We do not use third-party advertising or tracking cookies. You may control browser
              cookies in your browser settings; disabling strictly necessary cookies will impair
              your ability to use the Service.
            </p>
          </section>

          {/* 11. Children */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">11. Children's Data</h2>
            <p className="text-muted-foreground mt-2">
              We ask for a date of birth when an account is created. If it shows the person is under
              18, we ask for their parent's or legal guardian's email address and send the parent or
              guardian a link to give or refuse permission. Until they give permission, the account
              cannot be used and we hold only what was entered at sign-up, for no other purpose. If
              they refuse, or do not answer within 7 days, we delete the account and the young
              person's details, keeping only a record that permission was refused or not given. We
              keep a record of who gave permission and when. A parent or guardian can withdraw
              permission, or ask to see or delete their child's data, by writing to
              privacy@latlens.com. If you believe a child is using Latlens without permission,
              contact us and we will act without delay.
            </p>
          </section>

          {/* 12. Policy Changes */}
          <section>
            <h2 className="text-2xl font-semibold text-foreground">12. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground mt-2">
              We may update this Privacy Policy to reflect changes in our practices or applicable
              law. Material changes will be communicated by email to registered users and/or by a
              prominent notice on the Service at least 30 days before taking effect. Your continued
              use of the Service after the effective date constitutes acceptance of the updated policy.
              We encourage you to review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section className="p-6 bg-accent/10 rounded-lg border border-accent/30">
            <h2 className="text-2xl font-semibold text-foreground">13. Contact and Complaints</h2>
            <p className="text-muted-foreground mt-2 mb-4">
              For any questions, concerns, or to exercise your data subject rights, please contact us:
            </p>
            <div className="space-y-2 text-muted-foreground">
              <p><strong>Data Protection Officer:</strong> <a href="mailto:dpo@latlens.com" className="text-accent-text hover:underline">dpo@latlens.com</a></p>
              <p><strong>Privacy Enquiries:</strong> <a href="mailto:privacy@latlens.com" className="text-accent-text hover:underline">privacy@latlens.com</a></p>
              <p><strong>Support:</strong> <a href="mailto:support@latlens.com" className="text-accent-text hover:underline">support@latlens.com</a></p>
              <p><strong>Postal Address:</strong> Data Protection Officer, Latlens Technologies, 38b, Ondo Street, New Bodija, Ibadan, Oyo State, Nigeria</p>
            </div>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Supervisory Authority:</strong> If you are not satisfied with our response to your complaint,
                you may refer the matter to the <strong>Nigeria Data Protection Commission (NDPC)</strong>,
                the national supervisory authority for data protection in Nigeria.
                Website:{' '}
                <a href="https://ndpb.gov.ng" className="underline" target="_blank" rel="noopener noreferrer">ndpb.gov.ng</a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
